import React, { useState, useEffect } from 'react'
import { CheckIcon } from '@heroicons/react/outline'
import TagService from '../../../services/TagService';
import ClientService from '../../../services/ClientService';
import { nextCardNumber } from '../../../services/CardNumber';
import Modal from '../../common/Modal/Modal';
import MeasurementForm from '../MeasurementForm/MeasurementForm';
import {
    TextField, TextAreaField, YesNoField, SingleSelectField, TagsField, DateField,
    SectionTitle, toIsoDate
} from './fields';

// ============================================================================
// KARTON KLIJENTA
// - podeljen u sekcije, dve/tri kolone
// - vrednosti za filtriranje biraju se iz sifarnika (padajuci meniji sa
//   dodavanjem u hodu); stari slobodni upisi prikazuju se sivo ispod polja
// - sekcije Trudnoca / Posao / Skola / Aktivnost prikazuju se samo kad su
//   relevantne
// Stara tekstualna polja se i dalje salju na server neizmenjena, tako da se
// nista ne gubi kod postojecih klijenata.
// ============================================================================

const GENDER_OPTIONS = ['Ženski', 'Muški'];
const PACKAGE_OPTIONS = ['NutriM30', 'NutriM60', 'NutriM Premium'];
const MEALS_OPTIONS = ['2', '3', '4', '5', '6'];
const WATER_OPTIONS = ['Manje od 1l', '1 - 1.5l', '1.5 - 2l', '2 - 3l', 'Više od 3l'];
const INTENSITY_OPTIONS = ['Nizak', 'Umeren', 'Visok'];

const ClientsForm = ({ userData, modalTitle, setIsOpen, addClient, clients }) => {

    // ---- stanje: svako polje kartona ----
    const [needsAPlan, setNeedsAPlan] = useState(userData.needsAPlan || false);
    const [packageType, setPackageType] = useState(userData.packageType || '');
    const [targetWeight, setTargetWeight] = useState(userData.targetWeight || '');
    const [personalCardNumber, setPersonalCardNumber] = useState(() => {
        if (userData.personalCardNumber) return userData.personalCardNumber;   // postojeci klijent
        if (userData.id) return '';                                            // stari klijent bez broja
        if (Array.isArray(clients)) return nextCardNumber(clients);            // spisak vec imamo
        return '';                                                             // ceka se ucitavanje spiska
    });

    // Broj lične kartice se racuna iz svih postojecih klijenata. Karton se
    // otvara sa vise mesta (Klijenti, Dashboard); tamo gde spisak nije prosledjen
    // ucitavamo ceo spisak ovde, da bi novi broj uvek bio ispravan.
    useEffect(() => {
        const jeNoviKlijent = !userData.id && !userData.personalCardNumber;
        if (jeNoviKlijent && !Array.isArray(clients)) {
            ClientService.getClients()
                .then((response) => {
                    setPersonalCardNumber(nextCardNumber(response.data));
                })
                .catch(() => { /* ako ne uspe, ostaje prazno da se moze rucno upisati */ });
        }
    }, []);
    const [date, setDate] = useState(toIsoDate(userData.date) || userData.date || '');
    const [name, setName] = useState(userData.name || '');
    const [gender, setGender] = useState(userData.gender || '');
    const [birthday, setBirthday] = useState(toIsoDate(userData.birthday) || userData.birthday || '');
    const [email, setEmail] = useState(userData.email || '');
    const [phoneNumber, setPhoneNumber] = useState(userData.phoneNumber || '');
    const [height, setHeight] = useState(userData.height || '');
    const [weight, setWeight] = useState(userData.weight || '');
    const [size, setSize] = useState(userData.size || '');
    // Cilj je sada lista. Ako klijent jos nema listu a ima stari tekst
    // (javna prijava spaja ciljeve zarezima), razdvajamo ga nazad u stavke.
    const [goalList, setGoalList] = useState(() => {
        if (Array.isArray(userData.goalList) && userData.goalList.length > 0)
            return userData.goalList;
        if (userData.goal && String(userData.goal).trim())
            return String(userData.goal).split(',').map(g => g.trim()).filter(g => g.length > 0);
        return [];
    });

    // zdravlje - nove liste + stari tekstovi (salju se dalje netaknuti)
    const [diagnosisList, setDiagnosisList] = useState(userData.diagnosisList || []);
    const [allergyList, setAllergyList] = useState(userData.allergyList || []);
    const [intoleranceList, setIntoleranceList] = useState(userData.intoleranceList || []);
    const [medicineList, setMedicineList] = useState(userData.medicineList || []);
    const [dislikedFoodList, setDislikedFoodList] = useState(userData.dislikedFoodList || []);
    const [likedFoodList, setLikedFoodList] = useState(userData.likedFoodList || []);

    const [constipationIssues, setConstipationIssues] = useState(userData.constipationIssues || '');
    const [bloodPressureIssues, setBloodPressureIssues] = useState(userData.bloodPressureIssues || '');
    const [bloodResults, setBloodResults] = useState(userData.bloodResults || '');
    const [hasAllergies, setHasAllergies] = useState(userData.hasAllergies || false);

    // ishrana i navike
    const [dayDescription, setDayDescription] = useState(userData.dayDescription || '');
    const [breakfastDescription, setBreakfastDescription] = useState(userData.breakfastDescription || '');
    const [lunchDescription, setLunchDescription] = useState(userData.lunchDescription || '');
    const [dinnerDescription, setDinnerDescription] = useState(userData.dinnerDescription || '');
    const [snackDescription, setSnackDescription] = useState(userData.snackDescription || '');
    const [weekendDescription, setWeekendDescription] = useState(userData.weekendDescription || '');
    const [isCookingString, setIsCookingString] = useState(userData.isCookingString || '');
    const [numberOfMeals, setNumberOfMeals] = useState(userData.numberOfMeals || '');
    const [waterAmount, setWaterAmount] = useState(userData.waterAmount || '');
    const [isSmoker, setIsSmoker] = useState(userData.isSmoker || '');
    const [coffee, setCoffee] = useState(userData.coffee || '');
    const [tea, setTea] = useState(userData.tea || '');
    const [softDrinks, setSoftDrinks] = useState(userData.softDrinks || '');
    const [alcoholDrinks, setAlcoholDrinks] = useState(userData.alcoholDrinks || '');

    // posao i skola
    const [isEmployed, setIsEmployed] = useState(userData.isEmployed || '');
    const [job, setJob] = useState(userData.job || '');
    const [workSchedule, setWorkSchedule] = useState(userData.workSchedule || '');
    const [isWorkingNightShifts, setIsWorkingNightShifts] = useState(userData.isWorkingNightShifts || '');
    const [worksOnWeekends, setWorksOnWeekends] = useState(userData.worksOnWeekends || '');
    const [hasBreaks, setHasBreaks] = useState(userData.hasBreaks || '');
    const [canBringFoodToWork, setCanBringFoodToWork] = useState(userData.canBringFoodToWork || '');
    const [isStudent, setIsStudent] = useState(userData.isStudent || '');
    const [schoolSchedule, setSchoolSchedule] = useState(userData.schoolSchedule || '');
    const [canBringFoodToSchool, setCanBringFoodToSchool] = useState(userData.canBringFoodToSchool || '');

    // fizicka aktivnost
    const [isPhysicallyActive, setIsPhysicallyActive] = useState(userData.isPhysicallyActive || '');
    const [physicalActivity, setPhysicalActivity] = useState(userData.physicalActivity || '');
    const [physicalActivityIntensity, setPhysicalActivityIntensity] = useState(userData.physicalActivityIntensity || '');
    const [physicalActivityAmount, setPhysicalActivityAmount] = useState(userData.physicalActivityAmount || '');
    const [physicalActivityDays, setPhysicalActivityDays] = useState(userData.physicalActivityDays || '');
    const [physicalActivityTime, setPhysicalActivityTime] = useState(userData.physicalActivityTime || '');

    // trudnoca
    const [isPregnant, setIsPregnant] = useState(userData.isPregnant || '');
    const [pregnancyMonth, setPregnancyMonth] = useState(userData.pregnancyMonth || '');
    const [pregnancyWeightStart, setPregnancyWeightStart] = useState(userData.pregnancyWeightStart || '');
    const [pregnancyWeightGain, setPregnancyWeightGain] = useState(userData.pregnancyWeightGain || '');
    const [breastFeeding, setBreastFeeding] = useState(userData.breastFeeding || '');

    // napomene
    const [note, setNote] = useState(userData.note || '');
    const [corrections, setCorrections] = useState(userData.corrections || '');

    // ---- sifarnik ----
    const [tags, setTags] = useState({ diagnosis: [], allergy: [], intolerance: [], medicine: [], food: [], goal: [] });
    // Pune stavke sa ID-jem (za izmenu/brisanje u sifarniku)
    const [tagItems, setTagItems] = useState({ diagnosis: [], allergy: [], intolerance: [], medicine: [], food: [], goal: [] });

    const loadTags = () => {
        TagService.getTags().then((response) => {
            const grouped = { diagnosis: [], allergy: [], intolerance: [], medicine: [], food: [], goal: [] };
            const items = { diagnosis: [], allergy: [], intolerance: [], medicine: [], food: [], goal: [] };
            (response.data || []).forEach((tag) => {
                if (tag && tag.category && tag.name && grouped[tag.category]) {
                    grouped[tag.category].push(tag.name);
                    items[tag.category].push({ id: tag.id, name: tag.name });
                }
            });
            Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.localeCompare(b, 'sr')));
            Object.keys(items).forEach((k) => items[k].sort((a, b) => a.name.localeCompare(b.name, 'sr')));
            setTags(grouped);
            setTagItems(items);
        }).catch(() => { /* meniji rade i bez sifarnika, samo bez ponudjenih opcija */ });
    };

    useEffect(() => {
        loadTags();
    }, []);

    // Izmena imena stavke u sifarniku; osvezava spisak i vec izabrane vrednosti
    const renameTag = (category, id, oldName, newName) => {
        const name = String(newName || '').trim();
        if (!name || name === oldName) return;
        TagService.updateTag(id, name).then(() => {
            loadTags();
            // ako je stavka bila izabrana kod ovog klijenta, preimenuj i tamo
            renameInSelected(category, oldName, name);
        }).catch(() => { alert('Izmena nije uspela.'); });
    };

    // Brisanje stavke iz sifarnika (ne dira vec sacuvane klijente)
    const removeTag = (category, id) => {
        TagService.deleteTag(id).then(() => loadTags())
            .catch(() => { alert('Brisanje nije uspelo.'); });
    };

    // Kad se stavka preimenuje, promeni je i u trenutno izabranim vrednostima
    const renameInSelected = (category, oldName, newName) => {
        const swap = (list, setter) => {
            if ((list || []).includes(oldName))
                setter(list.map((v) => (v === oldName ? newName : v)));
        };
        if (category === 'goal') swap(goalList, setGoalList);
        if (category === 'diagnosis') swap(diagnosisList, setDiagnosisList);
        if (category === 'allergy') swap(allergyList, setAllergyList);
        if (category === 'intolerance') swap(intoleranceList, setIntoleranceList);
        if (category === 'medicine') swap(medicineList, setMedicineList);
        if (category === 'food') { swap(dislikedFoodList, setDislikedFoodList); swap(likedFoodList, setLikedFoodList); }
    };

    // nova stavka se trajno upise u sifarnik i odmah doda u lokalnu listu opcija
    const createTag = (category) => (tagName) => {
        TagService.addTag(category, tagName).then(() => {
            loadTags();  // osvezava i ID-jeve za izmenu/brisanje
        }).catch(() => { /* vrednost ostaje kod klijenta i ako upis u sifarnik ne uspe */ });
    };

    // ---- uslovi prikaza sekcija ----
    const genderLower = String(gender).toLowerCase();
    const showPregnancy = genderLower.startsWith('ž') || genderLower.startsWith('z') || String(isPregnant).toLowerCase() === 'da';
    const showWorkDetails = String(isEmployed).toLowerCase() === 'da';
    const showSchoolDetails = String(isStudent).toLowerCase() === 'da';
    const showActivityDetails = String(isPhysicallyActive).toLowerCase() === 'da';
    const showPregnancyDetails = String(isPregnant).toLowerCase() === 'da';

    const formValues = {
        needsAPlan, personalCardNumber, date, name, gender, birthday, email, phoneNumber,
        height, weight, size,
        packageType,
        targetWeight,
        goal: userData.goal || '',   // stari tekst ostaje netaknut
        goalList,
        // stari tekstovi idu dalje netaknuti
        healthIssues: userData.healthIssues || '',
        hasAllergies,
        allergiesString: userData.allergiesString || '',
        foodIntolerance: userData.foodIntolerance || '',
        medicines: userData.medicines || '',
        dislikesFood: userData.dislikesFood || '',
        likesFood: userData.likesFood || '',
        // nove liste iz sifarnika
        diagnosisList, allergyList, intoleranceList, medicineList, dislikedFoodList, likedFoodList,
        constipationIssues, bloodPressureIssues, bloodResults,
        breakfastDescription, dayDescription, lunchDescription, dinnerDescription,
        snackDescription, weekendDescription, isCookingString,
        numberOfMeals, waterAmount, isSmoker, coffee, tea, softDrinks, alcoholDrinks,
        isEmployed, job, workSchedule, isWorkingNightShifts, worksOnWeekends, hasBreaks,
        canBringFoodToWork, isStudent, schoolSchedule, canBringFoodToSchool,
        isPhysicallyActive, physicalActivity, physicalActivityIntensity,
        physicalActivityAmount, physicalActivityDays, physicalActivityTime,
        isPregnant, pregnancyMonth, pregnancyWeightStart, pregnancyWeightGain, breastFeeding,
        note, corrections,
    };

    const viewMeasurements = (e) => {
        e.preventDefault();
        window.open("/#/home/measurements/" + userData.id)
    }

    // Modal "Dodaj Mere" unutar kartona (ne otvara novi prozor)
    const [showMeasureModal, setShowMeasureModal] = useState(false);
    const [needsSaveFirst, setNeedsSaveFirst] = useState(false);
    const openAddMeasurement = (e) => {
        e.preventDefault();
        // Nov klijent još nema nalog u bazi - mere nemaju gde da se sačuvaju
        if (!userData.id) {
            setNeedsSaveFirst(true);
            return;
        }
        setShowMeasureModal(true);
    };
    // Snima nove mere za ovog klijenta, pa zatvara modal
    const saveMeasurement = (existing, formValues, clientId) => {
        const tempForm = { ...formValues, id: existing && existing.id };
        const req = tempForm.id
            ? ClientService.updateMeasurement(clientId, tempForm)
            : ClientService.addMeasurement(clientId, tempForm);
        req.then(() => setShowMeasureModal(false))
            .catch(() => setShowMeasureModal(false));
    };
    // Podaci o klijentu koje kalkulator koristi (pol, datum rođenja)
    const clientForCalc = { gender, birthday };

    const viewPlans = (e) => {
        e.preventDefault();
        window.open("/#/home/plan-history/" + userData.id)
    }

    const handleFormSubmit = (e) => {
        e.preventDefault();
        setIsOpen(false);
        addClient(userData, formValues);
    }

    const half = "w-full md:w-6/12 px-2 mt-3";
    const third = "w-full md:w-6/12 2xl:w-4/12 px-2 mt-3";
    const full = "w-full px-2 mt-3";

    return (
        <>
            <h3 className='text-center py-2 bg-bluePrimary text-white shadow-md font-medium'>{modalTitle}</h3>
            <form className="flex flex-wrap w-full mt-4 px-4 pb-4">
                <button type="submit" disabled style={{ display: "none" }} aria-hidden="true"></button>

                <div className="w-full flex justify-center gap-2 flex-wrap">
                    {userData.id &&
                        <button className="button bg-secondaryGradient my-2" onClick={viewMeasurements}>Pogledaj Mere</button>}
                    <button className="button bg-secondaryGradient my-2" onClick={openAddMeasurement}>Dodaj Mere</button>
                    {userData.id &&
                        <button className="button bg-secondaryGradient my-2" onClick={viewPlans}>Pogledaj Planove</button>}
                </div>

                {needsSaveFirst && !userData.id &&
                    <div className="w-full text-center text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
                        Prvo sačuvajte klijenta, pa ćete moći da dodate mere.
                    </div>
                }

                {showMeasureModal &&
                    <Modal setIsOpen={setShowMeasureModal}
                        modalContent={
                            <MeasurementForm
                                measurementData={{}}
                                modalTitle="Dodaj Mere"
                                clientId={userData.id}
                                selectedClient={clientForCalc}
                                setIsOpen={setShowMeasureModal}
                                setMeasurementsData={() => { }}
                                addMeasurement={saveMeasurement}
                                deleteMeasurement={() => setShowMeasureModal(false)}
                            />
                        } />
                }

                {/* ============ OSNOVNI PODACI ============ */}
                <SectionTitle>Osnovni podaci</SectionTitle>
                <div className={third}><TextField label="Ime i prezime" value={name} onChange={setName} /></div>
                <div className={third}><SingleSelectField label="Pol" value={gender} onChange={setGender} options={GENDER_OPTIONS} /></div>
                <div className={third}><DateField label="Datum rođenja" value={birthday} onChange={setBirthday} /></div>
                <div className={third}><TextField label="Email" value={email} onChange={setEmail} /></div>
                <div className={third}><TextField label="Broj telefona" value={phoneNumber} onChange={setPhoneNumber} /></div>
                <div className={third}><TextField label="Broj lične kartice" value={personalCardNumber} onChange={setPersonalCardNumber} /></div>
                <div className={third}><DateField label="Datum prvog dolaska" value={date} onChange={setDate} /></div>
                <div className="w-full px-2 mt-3">
                    <TagsField label="Cilj (može više njih)" value={goalList} onChange={setGoalList}
                        tagOptions={tags.goal} onCreate={createTag('goal')} manageItems={tagItems.goal} onRename={(id, oldName, newName) => renameTag('goal', id, oldName, newName)} onDelete={(id) => removeTag('goal', id)}
                        placeholder="Izaberi cilj ili ukucaj svoj..." />
                </div>
                <div className={third}>
                    <SingleSelectField label="Paket" value={packageType} onChange={setPackageType} options={PACKAGE_OPTIONS} />
                </div>
                <div className={third}>
                    <TextField label="Ciljna kilaža (kg)" value={targetWeight} onChange={setTargetWeight} placeholder="npr. 68" />
                </div>
                <div className={third}>
                    <div className="input-group checkbox mt-6">
                        <input type="checkbox" id="needsAPlan" defaultChecked={needsAPlan} onChange={() => setNeedsAPlan(!needsAPlan)} className="input-field" />
                        <label htmlFor="needsAPlan" className="font-medium text-xs text-gray-500"><span className='check'><CheckIcon /></span>Potreban je novi plan</label>
                    </div>
                </div>

                {/* ============ ZDRAVLJE ============ */}
                <SectionTitle>Zdravlje</SectionTitle>
                <div className={half}>
                    <TagsField label="Dijagnoze / zdravstveni problemi" value={diagnosisList} onChange={setDiagnosisList}
                        tagOptions={tags.diagnosis} onCreate={createTag('diagnosis')} manageItems={tagItems.diagnosis} onRename={(id, oldName, newName) => renameTag('diagnosis', id, oldName, newName)} onDelete={(id) => removeTag('diagnosis', id)} oldValue={userData.healthIssues} />
                </div>
                <div className={half}>
                    <TagsField label="Alergije" value={allergyList} onChange={setAllergyList}
                        tagOptions={tags.allergy} onCreate={createTag('allergy')} manageItems={tagItems.allergy} onRename={(id, oldName, newName) => renameTag('allergy', id, oldName, newName)} onDelete={(id) => removeTag('allergy', id)} oldValue={userData.allergiesString} />
                </div>
                <div className={half}>
                    <TagsField label="Intolerancije na hranu" value={intoleranceList} onChange={setIntoleranceList}
                        tagOptions={tags.intolerance} onCreate={createTag('intolerance')} manageItems={tagItems.intolerance} onRename={(id, oldName, newName) => renameTag('intolerance', id, oldName, newName)} onDelete={(id) => removeTag('intolerance', id)} oldValue={userData.foodIntolerance} />
                </div>
                <div className={half}>
                    <TagsField label="Lekovi" value={medicineList} onChange={setMedicineList}
                        tagOptions={tags.medicine} onCreate={createTag('medicine')} manageItems={tagItems.medicine} onRename={(id, oldName, newName) => renameTag('medicine', id, oldName, newName)} onDelete={(id) => removeTag('medicine', id)} oldValue={userData.medicines} />
                </div>
                <div className={third}><YesNoField label="Problemi sa opstipacijom" value={constipationIssues} onChange={setConstipationIssues} /></div>
                <div className={third}><YesNoField label="Problemi sa krvnim pritiskom" value={bloodPressureIssues} onChange={setBloodPressureIssues} /></div>
                <div className={third}><YesNoField label="Pušač" value={isSmoker} onChange={setIsSmoker} /></div>
                <div className={full}><TextAreaField label="Rezultati krvne analize" value={bloodResults} onChange={setBloodResults} /></div>

                {/* ============ ISHRANA I NAVIKE ============ */}
                <SectionTitle>Ishrana i navike</SectionTitle>
                <div className={half}>
                    <TagsField label="Hrana koju ne voli" value={dislikedFoodList} onChange={setDislikedFoodList}
                        tagOptions={tags.food} onCreate={createTag('food')} manageItems={tagItems.food} onRename={(id, oldName, newName) => renameTag('food', id, oldName, newName)} onDelete={(id) => removeTag('food', id)} oldValue={userData.dislikesFood} />
                </div>
                <div className={half}>
                    <TagsField label="Hrana koju voli" value={likedFoodList} onChange={setLikedFoodList}
                        tagOptions={tags.food} onCreate={createTag('food')} manageItems={tagItems.food} onRename={(id, oldName, newName) => renameTag('food', id, oldName, newName)} onDelete={(id) => removeTag('food', id)} oldValue={userData.likesFood} />
                </div>
                <div className={third}><SingleSelectField label="Broj obroka" value={numberOfMeals} onChange={setNumberOfMeals} options={MEALS_OPTIONS} /></div>
                <div className={third}><SingleSelectField label="Količina vode koju unosi" value={waterAmount} onChange={setWaterAmount} options={WATER_OPTIONS} /></div>
                <div className={third}><YesNoField label="Kuva" value={isCookingString} onChange={setIsCookingString} /></div>
                <div className={third}><TextField label="Kafa (koliko dnevno)" value={coffee} onChange={setCoffee} /></div>
                <div className={third}><TextField label="Čaj" value={tea} onChange={setTea} /></div>
                <div className={third}><TextField label="Bezalkoholna pića" value={softDrinks} onChange={setSoftDrinks} /></div>
                <div className={third}><TextField label="Alkoholna pića" value={alcoholDrinks} onChange={setAlcoholDrinks} /></div>
                <div className={half}><TextAreaField label="Opis dana" value={dayDescription} onChange={setDayDescription} /></div>
                <div className={half}><TextAreaField label="Opis vikenda" value={weekendDescription} onChange={setWeekendDescription} /></div>
                <div className={half}><TextAreaField label="Opis doručka" value={breakfastDescription} onChange={setBreakfastDescription} rows={2} /></div>
                <div className={half}><TextAreaField label="Opis ručka" value={lunchDescription} onChange={setLunchDescription} rows={2} /></div>
                <div className={half}><TextAreaField label="Opis večere" value={dinnerDescription} onChange={setDinnerDescription} rows={2} /></div>
                <div className={half}><TextAreaField label="Opis užine" value={snackDescription} onChange={setSnackDescription} rows={2} /></div>

                {/* ============ POSAO I ŠKOLA ============ */}
                <SectionTitle>Posao i škola</SectionTitle>
                <div className={third}><YesNoField label="Zaposlen/a" value={isEmployed} onChange={setIsEmployed} /></div>
                {showWorkDetails && <>
                    <div className={third}><TextField label="Zaposlenje" value={job} onChange={setJob} /></div>
                    <div className={third}><TextField label="Raspored rada" value={workSchedule} onChange={setWorkSchedule} placeholder="npr. 9-17h" /></div>
                    <div className={third}><YesNoField label="Radi u noćnim smenama" value={isWorkingNightShifts} onChange={setIsWorkingNightShifts} /></div>
                    <div className={third}><YesNoField label="Radi vikendom" value={worksOnWeekends} onChange={setWorksOnWeekends} /></div>
                    <div className={third}><YesNoField label="Ima pauze" value={hasBreaks} onChange={setHasBreaks} /></div>
                    <div className={third}><YesNoField label="Može da donese hranu na posao" value={canBringFoodToWork} onChange={setCanBringFoodToWork} /></div>
                </>}
                <div className={third}><YesNoField label="Student" value={isStudent} onChange={setIsStudent} /></div>
                {showSchoolDetails && <>
                    <div className={third}><TextField label="Školski raspored" value={schoolSchedule} onChange={setSchoolSchedule} /></div>
                    <div className={third}><YesNoField label="Može da donese hranu u školu" value={canBringFoodToSchool} onChange={setCanBringFoodToSchool} /></div>
                </>}

                {/* ============ FIZIČKA AKTIVNOST ============ */}
                <SectionTitle>Fizička aktivnost</SectionTitle>
                <div className={third}><YesNoField label="Fizički je aktivan/na" value={isPhysicallyActive} onChange={setIsPhysicallyActive} /></div>
                {showActivityDetails && <>
                    <div className={third}><TextField label="Fizička aktivnost" value={physicalActivity} onChange={setPhysicalActivity} placeholder="npr. teretana, trčanje" /></div>
                    <div className={third}><SingleSelectField label="Intenzitet" value={physicalActivityIntensity} onChange={setPhysicalActivityIntensity} options={INTENSITY_OPTIONS} /></div>
                    <div className={third}><TextField label="Koliko puta nedeljno" value={physicalActivityAmount} onChange={setPhysicalActivityAmount} /></div>
                    <div className={third}><TextField label="Dani aktivnosti" value={physicalActivityDays} onChange={setPhysicalActivityDays} placeholder="npr. pon, sre, pet" /></div>
                    <div className={third}><TextField label="Vreme aktivnosti" value={physicalActivityTime} onChange={setPhysicalActivityTime} placeholder="npr. ujutru" /></div>
                </>}

                {/* ============ TRUDNOĆA (samo za žene) ============ */}
                {showPregnancy && <>
                    <SectionTitle>Trudnoća</SectionTitle>
                    <div className={third}><YesNoField label="Trudna" value={isPregnant} onChange={setIsPregnant} /></div>
                    {showPregnancyDetails && <>
                        <div className={third}><TextField label="Mesec trudnoće" value={pregnancyMonth} onChange={setPregnancyMonth} /></div>
                        <div className={third}><TextField label="Težina na početku trudnoće" value={pregnancyWeightStart} onChange={setPregnancyWeightStart} /></div>
                        <div className={third}><TextField label="Kilaža dobijena u trudnoći" value={pregnancyWeightGain} onChange={setPregnancyWeightGain} /></div>
                    </>}
                    <div className={third}><YesNoField label="Doji" value={breastFeeding} onChange={setBreastFeeding} /></div>
                </>}

                {/* ============ NAPOMENE ============ */}
                <SectionTitle>Napomene</SectionTitle>
                <div className={half}><TextAreaField label="Beleška" value={note} onChange={setNote} /></div>
                <div className={half}><TextAreaField label="Ispravke" value={corrections} onChange={setCorrections} /></div>

                <div className="w-full flex justify-center mt-6">
                    <button type="submit" className="button without-icon bg-secondaryGradient" onClick={handleFormSubmit}>
                        Sačuvaj
                    </button>
                </div>
            </form>
        </>
    )
}

export default ClientsForm;
